/**
 * Formats a value for display in tables, charts, and metric cards.
 * Handles numbers, percentages, and currency.
 * @param {number|string} value - The value to format.
 * @param {Object} options - Formatting options.
 * @param {'number'|'percent'|'currency'} options.type - Type of value.
 * @param {number} [options.decimals=2] - Number of decimals.
 * @returns {string}
 */
export function formatValue(value, { type = 'number', decimals = 2, header } = {}) {
  if (value == null || value === '' || isNaN(Number(value))) return '0';

  let num = Number(value);

  // Clicks should always be integers (0 decimals)
  if (header === 'Clicks' || type === 'clicks') {
    return Math.round(num).toLocaleString();
  }

  if (type === 'percent') {
    // For Digital_Uplift%, ensure it shows 2 decimal places even for small values
    if (header === 'Digital_Uplift%') {
      return `${(num*100).toFixed(2)}%`;
    }
    // For ROI%, the value is already a percentage (0-100), so don't multiply by 100
    if (header === 'ROI%') {
      return `${num.toFixed(decimals)}%`;
    }
    // For Average Engagement Rate, always show 2 decimal places (similar to Digital_Uplift%)
    if (header === 'Average Engagement Rate' || header === 'Engagement Rate' || header === 'Eng. Rate') {
      return `${(num*100).toFixed(2)}%`;
    }
    return `${(num*100).toFixed(decimals)}%`;
  }

  if (type === 'currency') {
    // Handle K/M formatting for currency values
    if (Math.abs(num) >= 1e6) {
      return `$${(num / 1e6).toFixed(decimals)}M`;
    }
    if (Math.abs(num) >= 1e3) {
      return `$${(num / 1e3).toFixed(decimals)}K`;
    }
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  }

  // New: hL formatting for volume uplift
  if (type === 'hl') {
    return `${num.toFixed(decimals)} hL`;
  }

  // Total_Actuals and Total_Forecasts: No K/M formatting, just show number with 2 decimals
  if (header === 'Total_Actuals' || header === 'Total_Forecasts') {
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Default: number with K/M formatting
  if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(decimals) + 'M';
  if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(decimals) + 'K';
  
  // For total POCs, return as integer without decimals
  if (type === 'pocs') {
    return Math.round(num).toString();
  }
  
  return num.toFixed(decimals);
} 